<template>
  <div class="col px-0">
    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <div class="text-subtitle-1 text-sm-h6 text-md-h5 font-weight-bold text-center">
      Relatório de Mudas por Ciclo
    </div>

    <v-card>
      <v-card-title>
        <span>Filtros</span>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="harvestId"
              label="Safra"
              :items="harvestList"
              item-value="id"
              item-text="name"
              @change="loadCulturesFromHarvest"
              clearable
              @click:clear="harvestId = null; cultureList = []; cultureId = null"
              :loading="harvestListLoading"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="cultureId"
              label="Cultura"
              :items="harvestId ? cultureList : allCultureList"
              item-value="id"
              item-text="name"
              clearable
              @click:clear="cultureId = null"
              @change="loadPeriodsFromHarvestAndCulture"
              :loading="cultureListLoading"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="periodIds"
              label="Ciclo(s)"
              :items="harvestId ? periodList : allPeriodList"
              item-value="id"
              item-text="name"
              clearable
              @click:clear="periodIds = []"
              :loading="periodListLoading"
              multiple
              chips
              dense
              deletable-chips
            >
              <template v-slot:selection="data">
                <v-chip
                  v-bind="data.attrs"
                  :input-value="data.selected"
                  close
                  @click="data.select"
                  @click:close="removeSelectedPeriod(data.item)"
                >
                  {{ data.item.name }} 
                  <small class="font-italic font-weight-light ml-2 mt-1"> ({{data.item.additionalInfo}}) </small>
                  <small v-if="data.item.secondAdditionalInfo" class="font-italic font-weight-light ml-2 mt-1"> ({{data.item.secondAdditionalInfo}}) </small>
                </v-chip>
              </template>
              <template v-slot:item="data">
                <v-list-item-action>
                  <v-checkbox :input-value="data.attrs.inputValue"></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title class="text-subtitle-1">{{data.item.name}}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{data.item.additionalInfo}}
                    <br v-if="data.item.secondAdditionalInfo">
                    {{data.item.secondAdditionalInfo}}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="fieldIds"
              label="Talhão(ões)"
              :items="fieldsList"
              item-value="id"
              item-text="name"
              clearable
              @click:clear="fieldIds = []"
              :loading="harvestListLoading"
              multiple
              chips
              dense
              deletable-chips
            />
          </v-col>

          <!-- <v-col cols="12" sm="3">
            <v-autocomplete
              v-model="periodFilterModel"
              label="Data de Plantio"
              :items="periodFilterOptions"
              item-value="key"
              item-text="name"
            />
          </v-col>
          <v-col cols="12" sm="4" v-if="periodFilterModel == 'OTHER'">
            <DateRangeField
              :inputData.sync="periodFilterRangeModel"
              label="Período Customizado"
              :hasTodayDate="true"
            />
          </v-col> -->

          <v-col cols="12" sm="2">
            <v-btn 
              @click="loadSeedlingByPeriodReport"
              :disabled="false"
              class="mt-2"
            >
              <v-icon left>mdi-magnify</v-icon>
              Filtrar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <div>
      <BasePrint
        printableElementId="seedlingByPeriodReportTable"
        printButtonLabel="Imprimir"
        printTitle="Relatório de Mudas por Ciclo"
        @start-printing="printing = loading = true"
        @stop-printing="printing = loading = false"
        v-if="seedlingByPeriod"
        class="mt-3 mb-3"
      />
    </div>

    <div id="seedlingByPeriodReportTable">
      
      <v-row>
        <v-col cols="12" v-if="seedlingByPeriod">
          <BaseIndex
            ref="baseIndex"
            :loading="loading"
            :pageLoading="pageLoading"
            :table-headers="tableHeaders"
            :table-items="seedlingByPeriod"
            :indexNested="true"
            :disablePagination="true"
            :containTopToolbar="false"
            :maskFields="maskFields"
            :hasTableTitle="false"
            :itemClass="itemClasses"
            :hasDefaultPageTitle="false"
          />
        </v-col>
      </v-row>
    </div>
    
  </div>
</template>

<script>
import gql from 'graphql-tag'
import moment from "moment";
import BaseIndex from "@shared_components/base_index.vue";
import DateRangeField from "@shared_components/date_range_field.vue"
import BasePrint from "@shared_components/base_print.vue";

export default {
  data() {
    return {
      tableItems: [],
      seedlingByPeriod: null,
      pageLoading: false,
      loading: false,
      showEmptyItems: false,
      stockListLoading: false,
      stockList: [],
      stockIds: [],
      tableHeaders: [
        { text: "Ciclo", align: "left", sortable: false, value: "periodName" },
        { text: "Sub-cultura", align: "left", sortable: false, value: "subCultureName" },
        { text: "Variedade", align: "left", sortable: false, value: "varietyName" },
        { text: "Data do Plantio(s)", align: "left", sortable: false, value: "plantingDates" },
        { text: "Unid. de Plantio", align: "left", sortable: false, value: "plantingUnits" },
        { text: "Qtd. Plantada", align: "left", sortable: false, value: "toBeCropedQuantity" },
        { text: "Qtd. Colhida", align: "left", sortable: false, value: "cropedQuantity" },
        { text: "Qtd. em Estoque", align: "left", sortable: false, value: "storageQuantity" },
        { text: "Qtd. de Perdas", align: "left", sortable: false, value: "lossQuantity" },
        { text: "Qtd. de Vendas", align: "left", sortable: false, value: "saledQuantity" },
        { text: "Saldo", align: "left", sortable: false, value: "total" },
        // { text: "deleted", value: "_destroy", align: ' d-none', sortable: false, filter: value => value ? !value : true },
        // { text: "Ações", value: "action", sortable: false }
      ],
      maskFields: [
        { field: 'toBeCropedQuantity', mask: 'number' },
        { field: 'cropedQuantity', mask: 'number' },
        { field: 'storageQuantity', mask: 'number' },
        { field: 'lossQuantity', mask: 'number' },
        { field: 'saledQuantity', mask: 'number' },
        { field: 'total', mask: 'number' },
      ],
      harvestListLoading: false,
      harvestList: [],
      harvestId: null,
      cultureList: [],
      allCultureList: [],
      cultureListLoading: false,
      cultureId: null,
      periodListLoading: false,
      periodList: [],
      allPeriodList: [],
      periodIds: [],
      fieldsList: [],
      fieldIds: [],
      startDate: null,
      endDate: null,
      monthFilter: moment().format('YYYY-MM'),
      periodFilterOptions: [
        { "key": null, "name": "Sem filtro de data" },
        { "key": "DAY", "name": "Hoje" },
        { "key": "WEEK", "name": "Esta Semana" },
        { "key": "MONTH", "name": "Este Mês" },
        { "key": "OTHER", "name": "Customizado" },
      ],
      periodFilterModel: "MONTH",
      periodFilterRangeModel: [moment().startOf('week').format('YYYY-MM-DD'), moment().endOf('week').format('YYYY-MM-DD')]
    };
  },
  props: {
  },
  components: {
    BaseIndex: BaseIndex,
    DateRangeField: DateRangeField,
    BasePrint: BasePrint,
  },
  created() {
    // this.loadAllStocks();
    // this.loadSeedlingByPeriodReport();
    this.loadAllHarvests();
  },
  mounted() { 
  },
  computed: {
    // tableHeaders: function () { }
  },
  methods: {
    loadSeedlingByPeriodReport: function() {
      this.loading = true;
      this.runDateFilter();
      this.$apollo
        .query({
          query: LOAD_SEEDLING_BY_PERIOD_REPORT,
          variables: {
            harvestId: this.harvestId, 
            cultureId: this.cultureId, 
            periodIds: this.periodIds,
            fieldIds: this.fieldIds,
            startDate: this.startDate, 
            endDate: this.endDate
          },
          fetchPolicy: "no-cache"
        })
        .then(response => {
          this.seedlingByPeriod = response.data.accountContext.seedlingByPeriodReport;
          console.log("seedlingByPeriod", this.seedlingByPeriod);
        })
        .catch(response => console.log(response))
        .finally(() => (this.loading = false));
    },
    itemClasses: function(item) {
      if (item.total < 0) {
        return "negative_result";
      } else {
        return "positive_result";
      }
    },
    loadAllHarvests: function() {
        this.harvestListLoading = true;
        this.$apollo
          .query({
            query: LOAD_ALL_HARVEST_QUERY,
            fetchPolicy: "no-cache"
          })
          .then(response => {
            this.harvestList = response.data.accountContext.allHarvests;
            this.operationTypeList = response.data.accountGlobalSharedContext.allOperationTypes;
            this.fieldsList = response.data.groundContext.allFields;
            this.allCultureList = response.data.accountContext.allCulturesForAccount;
            this.allPeriodList = response.data.accountContext.allPeriods;
          })
          .catch(response => console.log(response))
          .finally(() => (this.harvestListLoading = false));
    },
    loadCulturesFromHarvest: function() {
      if (this.harvestId){
        this.cultureListLoading = true;
        this.$apollo
          .query({
            query: CULTURES_FROM_HARVEST_QUERY,
            variables: {
              harvestId: this.harvestId
            },
            fetchPolicy: "no-cache"
          })
          .then(response => {
            this.cultureList = response.data.accountContext.allCulturesFromHarvest;
            this.cultureId = null;
            this.periodIds = [];
          })
          .catch(response => console.log(response))
          .finally(() => (this.cultureListLoading = false));
        
        this.loadPeriodsFromHarvestAndCulture();
      }
    },
    loadPeriodsFromHarvestAndCulture: function() {
      this.periodListLoading = true;
      this.$apollo
        .query({
          query: PERIODS_FROM_HARVEST_AND_CULTURES_QUERY,
          variables: {
            harvestId: this.harvestId,
            cultureId: this.cultureId
          },
          fetchPolicy: "no-cache"
        })
        .then(response => {
          this.periodList = response.data.accountContext.allPeriods;
          this.periodIds = [];
        })
        .catch(response => console.log(response))
        .finally(() => (this.periodListLoading = false));
    },
    removeSelectedPeriod: function(item) {
      const index = this.periodIds.indexOf(item.id)
      if (index >= 0) this.periodIds.splice(index, 1)
    },
    runDateFilter: function() {
      if (this.periodFilterModel == null) {
        this.startDate = null;
        this.endDate   = null;
      } else if (this.periodFilterModel != "OTHER") {
        this.startDate = moment().startOf(this.periodFilterModel.toLocaleLowerCase()).format('YYYY-MM-DD');
        this.endDate   = moment().endOf(this.periodFilterModel.toLocaleLowerCase()).format('YYYY-MM-DD');
      } else {
        if (this.periodFilterRangeModel.length == 2) {
          this.startDate = this.periodFilterRangeModel[0];
          this.endDate   = this.periodFilterRangeModel[1];
        }
      }
      console.log(this.startDate, this.endDate);
    },
  }
};



const LOAD_SEEDLING_BY_PERIOD_REPORT = gql `
query ($harvestId: ID, $cultureId: ID, $periodIds: [ID!], $fieldIds: [ID!], $startDate: ISO8601Date, $endDate: ISO8601Date) {
  accountContext {
    seedlingByPeriodReport(harvestId: $harvestId, cultureId: $cultureId, periodIds: $periodIds, fieldIds: $fieldIds, startDate: $startDate, endDate: $endDate)
  }
}
`;

const LOAD_ALL_HARVEST_QUERY = gql `
  {
    accountContext {
      allHarvests {
        id
        name
      }
      allCulturesForAccount {
        id
        name
      }
      allPeriods {
        id
        name
        additionalInfo
        secondAdditionalInfo
      }
    }
    accountGlobalSharedContext {
      allOperationTypes {
        id
        name
      }
    }
    groundContext {
      allFields {
        id
        name
      }
    }
  }
`;

const CULTURES_FROM_HARVEST_QUERY = gql `
  query ($harvestId: ID!) {
    accountContext {
      allCulturesFromHarvest(harvestId: $harvestId){
        id
        name
      }
    }
  }
`;

const PERIODS_FROM_HARVEST_AND_CULTURES_QUERY = gql `
  query ($harvestId: ID, $cultureId: ID) {
    accountContext {
      allPeriods(harvestId: $harvestId, cultureId: $cultureId){
        id
        name
        additionalInfo
        secondAdditionalInfo
      }
    }
  }
`;

</script>

<style scoped>
  ::v-deep .positive_result {
    color: blue;
  }
  ::v-deep .negative_result {
    color: red;
  }
</style>
